import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowRight, Download, Instagram, Sparkles } from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";
import { usePageView } from "@/hooks/use-page-view";

const TITLE = "Welcome - your free Curiosity Starter Pack";
const DESC =
  "A 20-page premium trivia pack, free, no email needed. Then find out what kind of curious you are and get a feed built around it.";

export const Route = createFileRoute("/welcome")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://smartasaurus.com/welcome" },
      {
        property: "og:image",
        content: "https://smartasaurus.com/curiosity-starter-pack-cover.jpg",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      {
        name: "twitter:image",
        content: "https://smartasaurus.com/curiosity-starter-pack-cover.jpg",
      },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://smartasaurus.com/welcome" }],
  }),
  component: WelcomePage,
});

type VibeKey = "deep" | "deadly" | "body" | "ancient" | "impossible" | "mystery";

const QUESTIONS: Array<{
  q: string;
  options: Array<{ label: string; vibe: VibeKey }>;
}> = [
  {
    q: "Pick the sentence that makes you lean in.",
    options: [
      { label: "There is something down there we have never photographed alive.", vibe: "deep" },
      { label: "This can kill a human in under four minutes.", vibe: "deadly" },
      { label: "Your body is doing this right now and you can't feel it.", vibe: "body" },
      { label: "This walked the Earth before bones existed.", vibe: "ancient" },
    ],
  },
  {
    q: "Which one keeps you up at 2am?",
    options: [
      { label: "Physics that shouldn't be allowed to work.", vibe: "impossible" },
      { label: "A case nobody has solved yet.", vibe: "mystery" },
      { label: "Creatures that evolved something absurd.", vibe: "deep" },
      { label: "What's actually happening inside my head.", vibe: "body" },
    ],
  },
  {
    q: "Last one. What's your favourite kind of 'wait, WHAT?'",
    options: [
      { label: "Something tiny that's terrifyingly powerful.", vibe: "deadly" },
      { label: "Something so old it breaks my sense of time.", vibe: "ancient" },
      { label: "Something that shouldn't be real but is.", vibe: "impossible" },
      { label: "Something science still can't explain.", vibe: "mystery" },
    ],
  },
];

const TYPES: Record<VibeKey, { name: string; blurb: string }> = {
  deep: {
    name: "The Deep-Sea Type",
    blurb:
      "You're drawn to the parts of the planet we've barely looked at. Good news: that's most of it.",
  },
  deadly: {
    name: "The Danger Type",
    blurb:
      "You want the fact with teeth. Venom, pressure, speed - the things that don't negotiate.",
  },
  body: {
    name: "The Inside-Out Type",
    blurb:
      "The strangest machine you'll ever read about is the one you're operating right now.",
  },
  ancient: {
    name: "The Deep-Time Type",
    blurb:
      "You like facts that make a human lifespan look like a rounding error. Earth has plenty.",
  },
  impossible: {
    name: "The Impossible Type",
    blurb:
      "Common sense is a local rule. You enjoy watching reality ignore it completely.",
  },
  mystery: {
    name: "The Unsolved Type",
    blurb:
      "You'd rather have a great question than a tidy answer. Science has a backlog for you.",
  },
};

function WelcomePage() {
  usePageView("page");
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState<VibeKey[]>([]);

  const result: VibeKey | null =
    picks.length === QUESTIONS.length
      ? (Object.entries(
          picks.reduce<Record<string, number>>((acc, v) => {
            acc[v] = (acc[v] ?? 0) + 1;
            return acc;
          }, {}),
        ).sort((a, b) => b[1] - a[1])[0][0] as VibeKey)
      : null;

  function choose(vibe: VibeKey) {
    setPicks((p) => [...p, vibe]);
    setStep((s) => s + 1);
  }

  return (
    <div className="min-h-screen bg-background">
      <main id="main-content">
        {/* ---------------------------------------------------------- HERO */}
        <section className="border-b border-border/60 px-4 pb-14 pt-14 md:px-8 md:pb-20 md:pt-20">
          <div className="mx-auto grid max-w-5xl items-center gap-10 md:grid-cols-[1.1fr_0.9fr]">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
                · You're in
              </div>
              <h1 className="mt-3 font-display text-4xl uppercase leading-[0.95] md:text-6xl">
                Here's your{" "}
                <span className="text-gradient-curiosity">free card deck</span>.
                No email, no catch.
              </h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground">
                60 cards across animals, space, the human body and weird science -
                starting at a level anyone can win. Play it here with the
                scoreboard built in, or print the whole deck and play it at the
                table.
              </p>


              <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  to="/deck"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-4 font-display text-lg uppercase tracking-wide text-primary-foreground transition hover:opacity-90"
                >
                  <Sparkles className="h-5 w-5" />
                  Play the card deck
                </Link>
                <a
                  href="/dl/curiosity-starter-pack?ref=ig-dm"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-border px-7 py-4 font-display text-lg uppercase tracking-wide text-muted-foreground transition hover:text-foreground"
                >
                  <Download className="h-5 w-5" />
                  Or print the pack
                </a>
              </div>
              <p className="mt-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                60 cards · 3 levels · scores counted for you
              </p>

            </div>

            <div className="relative mx-auto w-full max-w-[320px]">
              <div className="absolute -inset-6 -z-10 rounded-3xl bg-primary/10 blur-2xl" />
              <img
                src="/curiosity-starter-pack-cover.jpg"
                alt="Cover of the Smartasaurus Curiosity Starter Pack"
                width={695}
                height={900}
                className="w-full rounded-xl border border-border/60 shadow-2xl"
              />
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------------- QUIZ */}
        <section className="border-b border-border/60 px-4 py-16 md:px-8 md:py-24">
          <div className="mx-auto max-w-3xl">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
              · While you're here
            </div>
            <h2 className="mt-3 font-display text-3xl uppercase leading-tight md:text-5xl">
              What kind of curious are you?
            </h2>
            <p className="mt-4 text-muted-foreground">
              Three questions. Then we'll point you at the discoveries built for
              your particular brand of "wait, WHAT?".
            </p>

            {result === null ? (
              <div className="mt-8 rounded-2xl border border-border/60 bg-card p-6 md:p-8">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  <Sparkles className="h-4 w-4 text-primary" />
                  Question {step + 1} of {QUESTIONS.length}
                </div>
                <p className="mt-4 font-display text-2xl uppercase leading-tight md:text-3xl">
                  {QUESTIONS[step].q}
                </p>
                <div className="mt-6 grid gap-3">
                  {QUESTIONS[step].options.map((o) => (
                    <button
                      key={o.label}
                      type="button"
                      onClick={() => choose(o.vibe)}
                      className="group flex items-center justify-between gap-4 rounded-xl border border-border/60 bg-background px-5 py-4 text-left text-base transition hover:border-primary hover:bg-primary/5"
                    >
                      <span>{o.label}</span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition group-hover:text-primary" />
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-8 rounded-2xl border border-primary/40 bg-card p-6 md:p-8">
                <div className="text-xs uppercase tracking-[0.2em] text-primary">
                  Your curiosity type
                </div>
                <p className="mt-3 font-display text-4xl uppercase leading-none md:text-6xl">
                  {TYPES[result].name}
                </p>
                <p className="mt-4 text-lg text-muted-foreground">
                  {TYPES[result].blurb}
                </p>
                <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/discoveries"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-4 font-display text-lg uppercase tracking-wide text-primary-foreground transition hover:opacity-90"
                  >
                    Start exploring
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      setPicks([]);
                      setStep(0);
                    }}
                    className="rounded-full border border-border px-7 py-4 font-display text-lg uppercase tracking-wide text-muted-foreground transition hover:text-foreground"
                  >
                    Take it again
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ---------------------------------------------------------- NEXT */}
        <section className="px-4 py-16 md:px-8 md:py-24">
          <div className="mx-auto grid max-w-5xl gap-4 md:grid-cols-3">
            {[
              {
                title: "Articles",
                body: "Search the full archive and follow any rabbit hole.",
                to: "/discoveries" as const,
              },
              {
                title: "Play",
                body: "Daily quiz, streaks, and a leaderboard you can lose to your own family.",
                to: "/play" as const,
              },
              {
                title: "Prove Me Wrong",
                body: "Pick what you believe. Watch the evidence disagree with you.",
                to: "/prove-me-wrong" as const,
              },
            ].map((card) => (
              <Link
                key={card.title}
                to={card.to}
                className="group rounded-2xl border border-border/60 bg-card p-6 transition hover:border-primary"
              >
                <div className="font-display text-2xl uppercase">{card.title}</div>
                <p className="mt-2 text-sm text-muted-foreground">{card.body}</p>
                <div className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary">
                  Open
                  <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>

          <div className="mx-auto mt-6 max-w-5xl rounded-2xl border border-border/60 bg-card p-6 md:flex md:items-center md:justify-between">
            <div>
              <div className="font-display text-2xl uppercase">
                The Broadcast Channel
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                One strange, true thing a few times a week. No inbox required.
              </p>
            </div>
            <a
              href="https://instagram.com/smartasaurusofficial"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 font-display text-base uppercase tracking-wide text-primary-foreground transition hover:opacity-90 md:mt-0"
            >
              <Instagram className="h-4 w-4" />
              Join on Instagram
            </a>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
